import React, { useEffect, useState } from "react";
import locationAutocomplete from "./locations/autocomplete";
import locationSearch from "./locations/coordinates";
import providerDirectorySearch from "./providerDirectory/providerDirectorySearch";
import Select from "react-select/async";
import { useDebouncedCallback } from "use-debounce";
import ProviderListings from "./ProviderListings";
import LoaderSpinner from "../LoaderSpinner/LoaderSpinner";
import { Listing, Location } from "./types";
import { useLocation } from "react-router-dom";

type SearchProps = {
  sessionToken: string;
  location: Location | undefined;
  listings: Array<Listing>;
  updateListings: (listings: Array<Listing>) => void;
  updateLocation: (location: Location) => void;
};

type Options = Array<{ value: string; label: string }>;

function Search({
  sessionToken,
  location,
  listings,
  updateListings,
  updateLocation,
}: SearchProps) {
  const [searchPerformed, setSearchPerformed] = useState(listings.length > 0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [placeId, setPlaceId] = useState<string>("");
  const [input, setInput] = useState<string>("");

  const queryParams = new URLSearchParams(useLocation().search);
  const [query] = useState<string>(queryParams.get("query") || "");

  // Used to mirror the options in the select box, this is mainly for creating
  // accessability elements
  const [options, setOptions] = useState<Options | null>(null);

  const _loadOptions = (
    input: string,
    callback: (results: Options) => void
  ) => {
    setInput(input);
    setIsSearching(true);

    locationAutocomplete(input, sessionToken).then((predictions) => {
      const locations = predictions.map((prediction) => {
        return {
          value: prediction.place_id,
          label: prediction.description.replace(/, Australia/, ""),
        };
      });

      setIsSearching(false);
      setOptions(locations);

      callback(locations);
    });
  };

  const providerSearch = (placeId: string) => {
    if (!placeId) return;
    setIsLoading(true);
    locationSearch(placeId, sessionToken).then((location) => {
      updateLocation(location);
      providerDirectorySearch({
        params: { latitude: location.lat, longitude: location.lng },
      }).then((results) => {
        updateListings(results);
        setIsLoading(false);
        setSearchPerformed(true);
      });
    });
  };

  useEffect(() => {
    if (!placeId) return;

    providerSearch(placeId);
  }, [placeId]);

  useEffect(() => {
    if (!query) return;

    locationAutocomplete(query, sessionToken).then((predictions) => {
      const firstPrediction = predictions[0];
      if (firstPrediction) {
        providerSearch(firstPrediction.place_id);
      }
    });
  }, [query]);

  const loadOptions = useDebouncedCallback(_loadOptions, 250);

  const NoOptionsMessage = (props: any) => {
    return null;
  };

  const feedbackMessage = (() => {
    if (isSearching === true) {
      return "Searching...";
    }

    if (options !== null && options.length === 0) {
      return `No address, postcode or suburb found for "${input}". Check your spelling and try again.`;
    }

    return "";
  })();

  return (
    <div className="grid lg:mx-32 mx-8 my-8">
      <h2 className="mb-2">Find an Optometrist near you</h2>

      <div className="mt-8 mb-64">
        <div className="p-4 bg-blue-100">
          <label htmlFor="find-provider" className="mb-2">
            Enter your address, postcode or nearest suburb
          </label>
          <Select
            menuIsOpen={options !== null && options.length > 0}
            inputId="find-provider"
            placeholder="e.g. Melbourne or 3000"
            loadOptions={loadOptions}
            defaultInputValue={query as string}
            defaultOptions={[]}
            isClearable={true}
            onChange={(e) => e && setPlaceId(e.value)}
            components={{ NoOptionsMessage }}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused
                  ? "rgb(75 85 99)"
                  : "rgb(75 85 99)",
              }),
            }}
          />
        </div>

        <div className="mt-2 text-center bg-gray-100 py-2" role="status">
          {feedbackMessage}
        </div>

        {isLoading && (
          <div className="mt-2" data-testid="loading-spinner">
            <LoaderSpinner />
          </div>
        )}

        {location && !isLoading && searchPerformed && (
          <div>
            <h3 className="my-8">Showing optometrists near {location.name}</h3>
            <ProviderListings listings={listings} />
          </div>
        )}

        <div className="mt-8 max-w-prose">
          <p className="my-4">
            Our Find a Provider page is powered by{" "}
            <a
              target="_blank"
              href="https://widget.nhsd.healthdirect.org.au/v1/widget/search?widgetId=9b5494f2-b4e6-495b-8d9c-e813dcebb7ca&types=%5Bservices_types%5D%3Aoptometry+service"
            >
              Healthdirect's National Health Services Directory (NHSD)
            </a>
            .
          </p>

          <h2 className="my-4">Incorrect details in search results</h2>

          <p className="my-4">
            If you experience any problems using the National Health Services
            Directory, or if you have a general query or would like to give
            feedback,{" "}
            <a href="mailto:nhsd@healthdirect.org.au">
              please email the NHSD team
            </a>{" "}
            with the details of your query along with your name and contact
            details.
          </p>

          <h2 className="my-4">Register your Services</h2>

          <p className="my-4">
            If you are a provider and don't see your information here then
            please first check if it is an{" "}
            <a
              href="https://widget.nhsd.healthdirect.org.au/v1/widget/search?widgetId=9b5494f2-b4e6-495b-8d9c-e813dcebb7ca&types=%5Bservices_types%5D%3Aoptometry+service"
              target="_blank"
            >
              existing listing in the NHSD
            </a>
          </p>

          <p className="my-4">
            If your service is not already listed,{" "}
            <a
              href="https://healthdirect-serviceline.atlassian.net/servicedesk/customer/portal/3/create/13"
              target="_blank"
            >
              complete the NHSD online registration form
            </a>{" "}
            so it can be found on the KeepSight website.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Search;
